import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useStore } from '../store';
import type { Game, Goal, Share, Feedback, Profile, Save, GameOutcome } from '../types';
import GoalAnalytics from '../components/GoalAnalytics';
import GoalDiagram from '../components/GoalDiagram';
import ShotPositionDiagram from '../components/ShotPositionDiagram';
import ShotPositionAnalytics from '../components/ShotPositionAnalytics';
import FeedbackForm from '../components/FeedbackForm';
import FeedbackList from '../components/FeedbackList';
import { AlertCircle, FileDown, MessageSquare, Target, User } from 'lucide-react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import AnalyticsPDF from '../components/AnalyticsPDF';
import { BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar } from 'recharts';
import { useTranslation } from '../i18n/useTranslation';

interface GameWithGoals extends Game {
  goals: Goal[];
}

const formatLabel = (label: string) => {
  return label
    .replace(/ Goal$/, '')
    .replace(/ Shot$/, '')
    .replace(' / ', '/');
};

const countGoalTypes = (goals: Goal[], type: 'shotType' | 'gameSituation') => {
  const counts: Record<string, number> = {};
  goals.forEach(goal => {
    const value = goal[type];
    if (value) {
      counts[value] = (counts[value] || 0) + 1;
    }
  });
  return Object.entries(counts)
    .map(([name, value]) => ({ 
      name: formatLabel(name),
      fullName: name,
      value 
    }))
    .sort((a, b) => b.value - a.value);
};

const renderBarLabel = (props: any) => {
  const { x, y, width, height, name, value } = props;
  
  if (!name || value === undefined || width < 30 || height < 20) {
    return <text />;
  }
  
  const labelX = x + width / 2;
  const labelY = y + height / 2;
  const fullText = `${name} (${value})`;
  const charWidth = 7;
  const lineHeight = 14;
  
  // If bar is wide enough for full text on one line, show it
  if (width >= fullText.length * charWidth + 10) {
    return (
      <text
        x={labelX}
        y={labelY}
        fill='#ffffff'
        textAnchor='middle'
        dominantBaseline='central'
        fontSize={12}
        fontWeight={600}
      >
        {fullText}
      </text>
    );
  }
  
  // Calculate how many characters can fit per line
  const maxCharsPerLine = Math.floor((width - 10) / charWidth);
  
  // If we can fit at least 4 characters per line and have enough height for 2 lines
  if (maxCharsPerLine >= 4 && height >= 28) {
    const words = name.split(' ');
    const valueText = `(${value})`;
    
    // Try to fit name on first line, value on second
    if (name.length <= maxCharsPerLine && valueText.length <= maxCharsPerLine) {
      return (
        <text
          x={labelX}
          y={labelY}
          fill='#ffffff'
          textAnchor='middle'
          dominantBaseline='central'
          fontSize={11}
          fontWeight={600}
        >
          <tspan x={labelX} dy={-lineHeight / 2}>
            {name}
          </tspan>
          <tspan x={labelX} dy={lineHeight}>
            {valueText}
          </tspan>
        </text>
      );
    }
    
    // If name is too long, try to wrap it intelligently
    let line1 = '';
    let line2 = '';
    
    if (words.length > 1) {
      // Try to split by words
      line1 = words[0];
      line2 = words.slice(1).join(' ') + ` (${value})`;
      
      // If second line is too long, truncate and add value
      if (line2.length > maxCharsPerLine) {
        const remainingSpace = maxCharsPerLine - ` (${value})`.length - 3;
        if (remainingSpace > 0) {
          line2 = words.slice(1).join(' ').substring(0, remainingSpace) + `... (${value})`;
        } else {
          line2 = `(${value})`;
        }
      }
    } else {
      // Single word - split the word if necessary
      if (name.length <= maxCharsPerLine) {
        line1 = name;
        line2 = `(${value})`;
      } else {
        const splitPoint = Math.max(3, maxCharsPerLine - 3);
        line1 = name.substring(0, splitPoint) + '...';
        line2 = `(${value})`;
      }
    }
    
    return (
      <text
        x={labelX}
        y={labelY}
        fill='#ffffff'
        textAnchor='middle'
        dominantBaseline='central'
        fontSize={10}
        fontWeight={600}
      >
        <tspan x={labelX} dy={-lineHeight / 2}>
          {line1}
        </tspan>
        <tspan x={labelX} dy={lineHeight}>
          {line2}
        </tspan>
      </text>
    );
  }
  
  // For very narrow bars, just show the value if it fits
  if (width >= value.toString().length * charWidth + 5) {
    return (
      <text
        x={labelX}
        y={labelY}
        fill='#ffffff'
        textAnchor='middle'
        dominantBaseline='central'
        fontSize={10}
        fontWeight={600}
      >
        {value}
      </text>
    );
  }
  
  // Bar too narrow for any meaningful text
  return <text />;
};

const getOutcomeColor = (outcome: GameOutcome): string => {
  switch (outcome) {
    case 'win':
    case 'win-ot':
    case 'win-so':
      return 'text-green-600';
    case 'loss':
    case 'loss-ot':
    case 'loss-so':
      return 'text-red-600';
    default:
      return 'text-gray-900';
  }
};

const formatOutcome = (outcome: GameOutcome): string => {
  if (!outcome) return '—';
  return outcome.toUpperCase().replace('-', ' ');
};

function SharedView() {
  const { t } = useTranslation();
  const { shareId } = useParams();
  const user = useStore((state) => state.user);
  const [games, setGames] = useState<GameWithGoals[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [share, setShare] = useState<Share | null>(null);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [saves, setSaves] = useState<Save[]>([]);
  const [selectedGameForFeedback, setSelectedGameForFeedback] = useState<string | null>(null);
  const [sharedByProfile, setSharedByProfile] = useState<Profile | null>(null);

  useEffect(() => {
    const fetchSharedData = async () => {
      if (!shareId || !user) return;

      try {
        // First get the share document
        const shareDoc = await getDoc(doc(db, 'shares', shareId));
        if (!shareDoc.exists()) {
          setError(t('sharedView.shareNotFound'));
          setLoading(false);
          return;
        }

        const shareData = {
          id: shareDoc.id,
          ...shareDoc.data(),
          createdAt: shareDoc.data().createdAt.toDate()
        } as Share;
        setShare(shareData);

        // Fetch profile of user who shared
        const profileDoc = await getDoc(doc(db, 'profiles', shareData.createdBy));
        if (profileDoc.exists()) {
          setSharedByProfile({
            id: profileDoc.id,
            ...profileDoc.data(),
            createdAt: profileDoc.data().createdAt.toDate()
          } as Profile);
        }

        // Fetch games using Promise.all for parallel execution
        const gamePromises = shareData.gameIds.map(gameId => 
          getDoc(doc(db, 'games', gameId))
        );
        
        const gameSnapshots = await Promise.all(gamePromises);
        const gamesData: GameWithGoals[] = gameSnapshots
          .filter(snapshot => snapshot.exists())
          .map(snapshot => ({
            id: snapshot.id,
            ...snapshot.data(),
            date: new Date(snapshot.data()!.date),
            createdAt: new Date(snapshot.data()!.createdAt),
            goals: []
          })) as GameWithGoals[];

        // Fetch goals for all games in parallel
        const goalPromises = gamesData.map(game => 
          getDocs(query(
            collection(db, 'goals'),
            where('gameId', '==', game.id)
          ))
        );

        const goalSnapshots = await Promise.all(goalPromises);
        
        // Assign goals to their respective games
        gamesData.forEach((game, index) => {
          game.goals = goalSnapshots[index].docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: new Date(doc.data().createdAt)
          })) as Goal[];
        });

        setGames(gamesData);

        // Fetch feedback for all games in parallel
        const feedbackPromises = gamesData.map(game =>
          getDocs(query(
            collection(db, 'feedback'),
            where('gameId', '==', game.id),
            orderBy('createdAt', 'desc')
          ))
        );

        const feedbackSnapshots = await Promise.all(feedbackPromises);
        const allFeedback = feedbackSnapshots.flatMap(snapshot =>
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate()
          })) as Feedback[]
        );

        setFeedback(allFeedback);

        // Fetch saves for all games in parallel
        const savesPromises = gamesData.map(game =>
          getDocs(query(
            collection(db, 'saves'),
            where('gameId', '==', game.id),
            orderBy('createdAt', 'desc')
          ))
        );

        const savesSnapshots = await Promise.all(savesPromises);
        const allSaves = savesSnapshots.flatMap(snapshot =>
          snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt.toDate()
          })) as Save[]
        );

        setSaves(allSaves);
      } catch (err) {
        console.error('Error fetching shared data:', err);
        setError(t('sharedView.failedToLoadSharedData'));
      } finally {
        setLoading(false);
      }
    };

    fetchSharedData();
  }, [shareId, user]);

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1b3968] mb-4"></div>
          <p className="text-gray-600">{t('sharedView.loadingSharedAnalysis')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          {error}
        </div>
      </div>
    );
  }

  const allGoals = games.flatMap(game => game.goals);
  const totalGames = games.length;
  const totalShots = games.reduce((acc, game) => acc + (game.shotsFaced || 0), 0);
  const totalSaves = games.reduce((acc, game) => acc + (game.saves || 0), 0);
  const savePercentage = totalShots > 0 ? ((totalSaves / totalShots) * 100).toFixed(1) : '0.0';
  const goalsAgainst = allGoals.length;
  const averageGoalsPerGame = totalGames > 0 ? (goalsAgainst / totalGames).toFixed(2) : '0.00';

  const getPdfFilename = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    return `shared-analysis-${timestamp}.pdf`;
  };

  const selectedGame = selectedGameForFeedback 
    ? games.find(game => game.id === selectedGameForFeedback)
    : null;

  const selectedGameSaves = selectedGameForFeedback
    ? saves.filter(save => save.gameId === selectedGameForFeedback)
    : [];

  return (
    <div className='max-w-6xl mx-auto px-4 py-8'>
      <div className='flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4'>
        <div>
          <h1 className='text-2xl md:text-3xl font-bold text-primary-600 italic'>
            {t('sharedView.title')}
          </h1>
          {sharedByProfile && (
            <div className='mt-2 flex items-center gap-2 text-gray-600'>
              <User className='h-4 w-4' />
              <span>
                {t('sharedView.sharedBy')} {sharedByProfile.firstName} {sharedByProfile.lastName}{' '}
                ({sharedByProfile.team})
              </span>
            </div>
          )}
        </div>
        <PDFDownloadLink
          document={
            <AnalyticsPDF
              games={games}
              goals={allGoals}
              selectionMode='multiple'
            />
          }
          fileName={getPdfFilename()}
          className='text-[#1b3968] hover:text-[#1b3968]/80 flex items-center gap-2 px-4 py-2 border border-[#1b3968] rounded-md transition-colors'
        >
          {({ loading }) => (
            <>
              <FileDown className='h-5 w-5' />
              <span>{loading ? t('sharedView.preparingPDF') : t('sharedView.exportPDF')}</span>
            </>
          )}
        </PDFDownloadLink>
      </div>

      <div className='grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mb-8'>
        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-2 md:mb-4'>
            {t('sharedView.totalGoalsAgainst')}
          </h3>
          <div className='text-2xl md:text-3xl font-bold text-red-600'>
            {allGoals.length}
          </div>
          <p className='text-sm text-gray-500'>{t('sharedView.acrossGames', { count: games.length.toString() })}</p>
        </div>
        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-2 md:mb-4'>
            {t('sharedView.averageGoalsPerGame')}
          </h3>
          <div className='text-2xl md:text-3xl font-bold text-[#1b3968]'>
            {averageGoalsPerGame}
          </div>
          <p className='text-sm text-gray-500'>{t('sharedView.basedOnAllGames')}</p>
        </div>
        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h3 className='text-lg font-medium text-gray-900 mb-2 md:mb-4'>
            {t('sharedView.savePercentage')}
          </h3>
          <div className='text-2xl md:text-3xl font-bold text-green-600'>
            {savePercentage}%
          </div>
          <p className='text-sm text-gray-500'>{t('sharedView.overallSavePercentage')}</p>
        </div>
      </div>

      <div className='mt-12'>
        <h2 className='text-2xl font-bold mb-6 flex items-center gap-2'>
          <MessageSquare className='h-6 w-6' />
          {t('sharedView.feedback')}
        </h2>

        <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8'>
          <div>
            <div className='bg-white shadow rounded-lg p-4 md:p-6 mb-6'>
              <h3 className='text-lg font-medium mb-4'>
                {t('sharedView.selectGameForFeedback')}
              </h3>
              <div className='space-y-2'>
                {games.map((game) => (
                  <button
                    key={game.id}
                    onClick={() => setSelectedGameForFeedback(game.id)}
                    className={`w-full text-left glass-card p-4 rounded-2xl transition-all duration-300 hover:scale-[1.02] hover:shadow-lg group ${
                      selectedGameForFeedback === game.id
                        ? 'ring-2 ring-[#1b3968] bg-gradient-to-br from-[#1b3968] to-[#15304f] text-white shadow-xl'
                        : 'hover:ring-2 hover:ring-[#1b3968]/30 hover:bg-gradient-to-br hover:from-[#1b3968]/5 hover:to-transparent'
                    }`}
                  >
                    <div className='flex justify-between items-center'>
                      <div className='flex-1'>
                        <div
                          className={`font-semibold text-lg mb-1 transition-colors duration-300 ${
                            selectedGameForFeedback === game.id
                              ? 'text-white'
                              : 'text-heading group-hover:text-[#1b3968]'
                          }`}
                        >
                          {game.opponent}
                        </div>
                        <div
                          className={`text-sm font-medium ${
                            selectedGameForFeedback === game.id
                              ? 'text-white/80'
                              : 'text-muted'
                          }`}
                        >
                          {game.date.toLocaleDateString()}
                        </div>
                      </div>
                      {game.outcome && (
                        <div className='ml-4'>
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                              selectedGameForFeedback === game.id
                                ? 'bg-white/20 text-white'
                                : game.outcome.includes('win')
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {formatOutcome(game.outcome)}
                          </span>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {selectedGame && (
              <>
                {selectedGameSaves.length > 0 && (
                  <div className='bg-white shadow rounded-lg p-4 md:p-6 mb-6'>
                    <h3 className='text-lg font-medium mb-4'>
                      {t('sharedView.savesAndGoodThings')}
                    </h3>
                    <p className='text-gray-600 text-sm mb-4'>
                      {t('sharedView.highlightsAndMoments')}
                    </p>
                    <div className='space-y-4'>
                      {selectedGameSaves.map((save) => (
                        <div
                          key={save.id}
                          className='bg-gray-50 rounded-lg p-4 border border-gray-200'
                        >
                          <h4 className='font-medium text-gray-900 mb-2'>
                            {save.title}
                          </h4>
                          <p className='text-gray-600'>
                            {save.situationDescription}
                          </p>
                          <div className='mt-2 text-sm text-gray-500'>
                            {save.createdAt.toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className='bg-white shadow rounded-lg p-4 md:p-6 mb-6'>
                  <div className='flex items-center gap-2 mb-4'>
                    <Target className='h-5 w-5 text-[#1b3968]' />
                    <h3 className='text-lg font-medium'>{t('sharedView.goalsTimeline')}</h3>
                  </div>

                  <div className='space-y-4'>
                    {selectedGame.goals.length === 0 ? (
                      <p className='text-gray-500 text-center py-4'>
                        {t('sharedView.noGoalsRecorded')}
                      </p>
                    ) : (
                      selectedGame.goals.map((goal, index) => (
                        <div
                          key={goal.id}
                          className='bg-gray-50 rounded-lg p-4 border border-gray-200'
                        >
                          <div className='flex items-start'>
                            <div className='flex-shrink-0 bg-[#1b3968] text-white rounded-full h-6 w-6 flex items-center justify-center text-sm mr-3'>
                              {index + 1}
                            </div>
                            <div className='flex-1'>
                              <h4 className='font-medium text-gray-900'>
                                {goal.title}
                              </h4>
                              <p className='text-gray-600 mt-1'>
                                {goal.situationDescription}
                              </p>
                              <div className='mt-2 flex flex-wrap gap-2'>
                                {goal.shotType && (
                                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800'>
                                    {goal.shotType}
                                  </span>
                                )}
                                {goal.gameSituation && (
                                  <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800'>
                                    {goal.gameSituation}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}

            {selectedGameForFeedback && (
              <FeedbackForm
                gameId={selectedGameForFeedback}
                onFeedbackSubmitted={() => {
                  // Refresh feedback list
                  const fetchFeedback = async () => {
                    const feedbackQuery = query(
                      collection(db, 'feedback'),
                      where(
                        'gameId',
                        'in',
                        games.map((g) => g.id)
                      ),
                      orderBy('createdAt', 'desc')
                    );

                    const feedbackSnapshot = await getDocs(feedbackQuery);
                    const feedbackData = feedbackSnapshot.docs.map((doc) => ({
                      id: doc.id,
                      ...doc.data(),
                      createdAt: doc.data().createdAt.toDate(),
                    })) as Feedback[];

                    setFeedback(feedbackData);
                  };
                  fetchFeedback();
                }}
              />
            )}
          </div>

          <div className='bg-white shadow rounded-lg p-4 md:p-6'>
            <h3 className='text-lg font-medium mb-4'>{t('sharedView.allFeedback')}</h3>
            <FeedbackList
              feedback={feedback}
              onFeedbackDeleted={() => {
                // Refresh feedback list
                const fetchFeedback = async () => {
                  const feedbackQuery = query(
                    collection(db, 'feedback'),
                    where(
                      'gameId',
                      'in',
                      games.map((g) => g.id)
                    ),
                    orderBy('createdAt', 'desc')
                  );

                  const feedbackSnapshot = await getDocs(feedbackQuery);
                  const feedbackData = feedbackSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                    createdAt: doc.data().createdAt.toDate(),
                  })) as Feedback[];

                  setFeedback(feedbackData);
                };
                fetchFeedback();
              }}
              viewMode='received'
              gameOwners={{}}
              games={games.reduce(
                (acc, game) => ({ ...acc, [game.id]: game }),
                {}
              )}
            />
          </div>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8'>
        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h2 className='text-xl font-bold mb-4 md:mb-6'>
            {t('sharedView.goalDistributionHeatmap')}
          </h2>
          <div className='flex justify-center'>
            <GoalAnalytics
              goals={allGoals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
            />
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            {t('sharedView.heatmapExplanation')}
          </p>
        </div>

        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h2 className='text-xl font-bold mb-4 md:mb-6'>{t('sharedView.goalLocations')}</h2>
          <div className='flex justify-center'>
            <GoalDiagram
              onPositionSelect={() => {}}
              goals={allGoals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
              readOnly={true}
              hideNumbers={true}
            />
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            {t('sharedView.goalPointsExplanation')}
          </p>
        </div>

        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h2 className='text-xl font-bold mb-4 md:mb-6'>
            {t('analytics.shotDistributionHeatmap')}
          </h2>
          <div className='flex justify-center'>
            <ShotPositionAnalytics
              goals={allGoals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
            />
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            {t('sharedView.shotHeatmapExplanation')}
          </p>
        </div>

        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h2 className='text-xl font-bold mb-4 md:mb-6'>{t('sharedView.shotLocations')}</h2>
          <div className='flex justify-center'>
            <ShotPositionDiagram
              onPositionSelect={() => {}}
              goals={allGoals}
              width={Math.min(window.innerWidth - 40, 500)}
              height={Math.min(window.innerWidth - 40, 375)}
              readOnly={true}
              hideNumbers={true}
            />
          </div>
          <p className='mt-4 text-sm text-gray-600'>
            {t('sharedView.shotPointsExplanation')}
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 mt-8'>
        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h3 className='text-lg font-medium mb-4'>{t('sharedView.shotTypesDistribution')}</h3>
          <div className='h-[400px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={countGoalTypes(allGoals, 'shotType')}
                layout='vertical'
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id='sharedShotTypeGradient'
                    x1='0'
                    y1='0'
                    x2='1'
                    y2='0'
                  >
                    <stop offset='0%' stopColor='#3B82F6' />
                    <stop offset='50%' stopColor='#1D4ED8' />
                    <stop offset='100%' stopColor='#1E40AF' />
                  </linearGradient>
                </defs>
                <XAxis
                  type='number'
                  tickCount={
                    Math.max(
                      ...countGoalTypes(allGoals, 'shotType').map(
                        (item) => item.value
                      )
                    ) + 1
                  }
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                  axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={20}
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                  formatter={(value: number, name: string, props: any) => [
                    value,
                    props.payload.fullName,
                  ]}
                />
                <Bar
                  dataKey='value'
                  fill='url(#sharedShotTypeGradient)'
                  radius={[0, 6, 6, 0]}
                  stroke='rgba(255, 255, 255, 0.5)'
                  strokeWidth={1}
                  label={renderBarLabel}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className='bg-white shadow rounded-lg p-4 md:p-6'>
          <h3 className='text-lg font-medium mb-4'>
            Game Situations Distribution
          </h3>
          <div className='h-[400px]'>
            <ResponsiveContainer width='100%' height='100%'>
              <BarChart
                data={countGoalTypes(allGoals, 'gameSituation')}
                layout='vertical'
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id='sharedGameSituationGradient'
                    x1='0'
                    y1='0'
                    x2='1'
                    y2='0'
                  >
                    <stop offset='0%' stopColor='#10B981' />
                    <stop offset='50%' stopColor='#059669' />
                    <stop offset='100%' stopColor='#047857' />
                  </linearGradient>
                </defs>
                <XAxis
                  type='number'
                  tickCount={
                    Math.max(
                      ...countGoalTypes(allGoals, 'gameSituation').map(
                        (item) => item.value
                      )
                    ) + 1
                  }
                  domain={[0, 'dataMax']}
                  allowDecimals={false}
                  tick={{ fontSize: 12, fill: '#64748B', fontWeight: 500 }}
                  axisLine={{ stroke: '#E2E8F0', strokeWidth: 2 }}
                  tickLine={{ stroke: '#CBD5E1' }}
                />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={20}
                  tick={false}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                    fontSize: '14px',
                    fontWeight: '500',
                  }}
                  formatter={(value: number, name: string, props: any) => [
                    value,
                    props.payload.fullName,
                  ]}
                />
                <Bar
                  dataKey='value'
                  fill='url(#sharedGameSituationGradient)'
                  radius={[0, 6, 6, 0]}
                  stroke='rgba(255, 255, 255, 0.5)'
                  strokeWidth={1}
                  label={renderBarLabel}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SharedView;